import { Error, Loading } from '@hogwarts/ui-components-core';
import { orderBy } from 'lodash';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPreferences as UserPreferencesComponent } from '../../../components/Settings/UserPreferences';
import { AnalyticsContext, UserContext } from '../../../context';
import { useMutation, useQuery, useQueryResults } from '../../../hooks';
import { REQUEST_EMAIL_CHANGE, UPDATE_USER_PREFERENCES } from '../../../mutations';
import {
  GET_ALL_LANGUAGES,
  GET_CURRENT_USER,
  GET_LOCATIONS,
  GET_TIMEZONES,
  GET_USER_ORGANISATIONS,
} from '../../../queries';
import { AppToaster } from '../../../utils/toaster';

const getErrorMessage = (err: any): string => {
  const gqlMessage =
    err?.graphQLErrors?.[0]?.message ||
    err?.networkError?.result?.errors?.[0]?.message ||
    err?.networkError?.message;
  const raw = gqlMessage || err?.message || '';
  if (typeof raw === 'string') {
    // Apollo sometimes formats as "GraphQL error: <message>"
    const m = raw.match(/^GraphQL error:\s*(.+)$/i);
    if (m?.[1]) return m[1];
    return raw;
  }
  return 'Something went wrong';
};

const UserPreferences = () => {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const analytics = useContext(AnalyticsContext);

  const locationsQuery = useQuery(GET_LOCATIONS, {
    selector: 'locations',
    transform: (locations: { code: string; name: string }[]) => {
      return locations.map((location) => ({
        id: location.code,
        value: location.name,
      }));
    },
  });

  const timezonesQuery = useQuery(GET_TIMEZONES, {
    selector: 'timezones',
    transform: (
      timezones: { code: string; name: string; offset: number }[]
    ) => {
      return orderBy(
        timezones.map((timezone) => ({
          id: timezone.code,
          value: `${timezone.code} (UTC ${
            timezone.offset < 0 ? timezone.offset : `+${timezone.offset}`
          })`,
        })),
        ['offset', 'code']
      );
    },
  });

  const languagesQuery = useQuery(GET_ALL_LANGUAGES, {
    selector: 'languages',
    transform: (
      languages: { key: string; name: string; enabled: boolean }[]
    ) => {
      return languages
        .filter((language) => language.enabled)
        .map((language) => ({
          id: language.key,
          value: language.name,
        }));
    },
  });

  const organisationsQuery = useQuery(GET_USER_ORGANISATIONS, {
    selector: 'organisations',
    transform: (organisations: { key: string; name: string }[]) => {
      return orderBy(
        organisations.map((org) => ({
          id: org.key,
          value: org.name?.trim(),
        })),
        (o) => o.value.toLowerCase()
      );
    },
  });

  const [updateUserPreferences] = useMutation(UPDATE_USER_PREFERENCES, {
    refetchQueries: [{ query: GET_CURRENT_USER }],
  });
  const [requestEmailChange] = useMutation(REQUEST_EMAIL_CHANGE, {
    refetchQueries: [{ query: GET_CURRENT_USER }],
  });

  const { loading, error } = useQueryResults({
    languagesQuery,
    organisationsQuery,
    timezonesQuery,
    locationsQuery,
  });

  if (loading) {
    return <Loading />;
  }
  if (error) {
    return <Error />;
  }

  return (
    <UserPreferencesComponent
      organisations={organisationsQuery.data}
      languages={languagesQuery.data}
      timezones={timezonesQuery.data}
      // locations={data.locationsQuery}
      email={user.email}
      tempEmail={(user as any).tempEmail}
      firstname={user.firstname}
      lastname={user.lastname}
      preferences={user.preferences || {}}
      onSavePreferences={async ({
        email,
        firstname,
        lastname,
        ...values
      }) => {
        analytics.events.users.userPreferencesSave();

        try {
          const nextEmail =
            typeof email === 'string' ? email.trim().toLowerCase() : '';
          const currentEmail =
            typeof user.email === 'string' ? user.email.trim().toLowerCase() : '';

          if (nextEmail && nextEmail !== currentEmail) {
            const ok = window.confirm(
              t(
                `Change your email from "${currentEmail}" to "${nextEmail}"? This will affect how you log in.`
              )
            );
            if (!ok) {
              return;
            }

            await requestEmailChange({
              variables: {
                newEmail: nextEmail,
              },
            });
            AppToaster.show({
              message: t(`Verification email sent`),
              intent: 'success',
              icon: 'tick',
            });
          }

          await updateUserPreferences({
            variables: {
              firstname,
              lastname,
              updatedPreferences: {
                ...values,
              },
            },
          });
          AppToaster.show({
            message: t(`Save successful`),
            intent: 'success',
            icon: 'tick',
          });
        } catch (err) {
          // Use the same toaster key as Apollo global handler to avoid
          // double-toasting for the same underlying request.
          AppToaster.show(
            {
              message: t(getErrorMessage(err)),
              intent: 'danger',
              icon: 'error',
            },
            'apollo_error'
          );
        }
      }}
    />
  );
};

export default UserPreferences;
